import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

/**
 * Reference panels shown above the "Fill in the details" form on certain steps.
 * Each reference points to an earlier step's output (by prompt_key).
 *
 * Map keys are the CURRENT step's ai_prompt_key.
 * Each entry lists earlier outputs whose content the current step depends on.
 *
 * Steps 7-12 (funnel builder) are shared across all 3 paths with identical prompt keys,
 * so a single entry covers Fast Cash, Faceless Affiliate, and Digital Product.
 */
export const STEP_REFERENCES = {
  // ========== Fast Cash Path (steps 1-5) ==========
  fast_cash_offer_page_copy: [
    { prompt_key: 'fast_cash_offer_generator', label: 'Your Quick Offer', from_step: 1 },
  ],
  fast_cash_pricing_helper: [
    { prompt_key: 'fast_cash_offer_generator', label: 'Your Quick Offer', from_step: 1 },
  ],
  fast_cash_outreach_script: [
    { prompt_key: 'fast_cash_offer_generator', label: 'Your Quick Offer', from_step: 1 },
    { prompt_key: 'fast_cash_offer_page_copy', label: 'Your Offer Page Copy', from_step: 2 },
  ],
  fast_cash_followup_script: [
    { prompt_key: 'fast_cash_outreach_script', label: 'Your Outreach Script', from_step: 4 },
  ],

  // ========== Faceless Affiliate Path (steps 1-5) ==========
  affiliate_bridge_page_copy: [
    { prompt_key: 'affiliate_offer_angle_selector', label: 'Your Offer & Angle', from_step: 1 },
  ],
  affiliate_content_generator: [
    { prompt_key: 'affiliate_offer_angle_selector', label: 'Your Offer & Angle', from_step: 1 },
    { prompt_key: 'affiliate_bridge_page_copy', label: 'Your Bridge Page', from_step: 2 },
  ],
  affiliate_cta_generator: [
    { prompt_key: 'affiliate_offer_angle_selector', label: 'Your Offer & Angle', from_step: 1 },
    { prompt_key: 'affiliate_bridge_page_copy', label: 'Your Bridge Page', from_step: 2 },
  ],
  affiliate_tracking_notes_generator: [
    { prompt_key: 'affiliate_content_generator', label: 'Your Faceless Content', from_step: 3 },
    { prompt_key: 'affiliate_cta_generator', label: 'Your CTAs', from_step: 4 },
  ],

  // ========== Digital Product Path (steps 1-9) ==========
  product_content_generator: [
    { prompt_key: 'digital_product_outline_generator', label: 'Your Product Outline', from_step: 3 },
  ],
  product_packaging_guide: [
    { prompt_key: 'digital_product_outline_generator', label: 'Your Product Outline', from_step: 3 },
    { prompt_key: 'product_content_generator', label: 'Your Product Content', from_step: 6 },
  ],
  digital_product_funnel_copy: [
    { prompt_key: 'product_outcome_definer', label: 'Your Product Outcome', from_step: 5 },
    { prompt_key: 'product_packaging_guide', label: 'Your Product Package', from_step: 7 },
  ],
  digital_product_launch_posts: [
    { prompt_key: 'product_outcome_definer', label: 'Your Product Outcome', from_step: 5 },
    { prompt_key: 'digital_product_funnel_copy', label: 'Your Funnel Copy', from_step: 8 },
  ],

  // ========== Funnel Builder (steps 7-12, shared by all 3 paths) ==========
  funnel_offer_builder: [
    { prompt_key: 'funnel_headline_generator', label: 'Your Headline', from_step: 7 },
  ],
  funnel_content_builder: [
    { prompt_key: 'funnel_headline_generator', label: 'Your Headline', from_step: 7 },
    { prompt_key: 'funnel_offer_builder', label: 'Your Offer', from_step: 8 },
  ],
  funnel_cta_generator: [
    { prompt_key: 'funnel_offer_builder', label: 'Your Offer', from_step: 8 },
    { prompt_key: 'funnel_content_builder', label: 'Your Page Content', from_step: 9 },
  ],
  funnel_tech_setup: [
    { prompt_key: 'funnel_content_builder', label: 'Your Page Content', from_step: 9 },
    { prompt_key: 'funnel_cta_generator', label: 'Your Call to Action', from_step: 10 },
  ],
  funnel_launch_guide: [
    { prompt_key: 'funnel_offer_builder', label: 'Your Offer', from_step: 8 },
    { prompt_key: 'funnel_content_builder', label: 'Your Page Content', from_step: 9 },
    { prompt_key: 'funnel_cta_generator', label: 'Your Call to Action', from_step: 10 },
  ],
};

const ReferenceBlock = ({ output, ref_config, pathId }) => {
  const [open, setOpen] = useState(true);
  const testKey = ref_config.prompt_key;

  if (!output) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-3"
      >
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
          data-testid={`ref-missing-${testKey}`}
        >
          <Sparkles className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900 mb-0.5">
              {ref_config.label} hasn&apos;t been generated yet.
            </p>
            <p className="text-amber-800">
              Head back to{' '}
              <Link
                to={`/path/${pathId}/step/${ref_config.from_step}`}
                className="underline font-medium hover:text-amber-950"
                data-testid={`ref-missing-link-${testKey}`}
              >
                Step {ref_config.from_step}
              </Link>
              {' '}and generate it first — you&apos;ll need it here to guide this step.
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-3"
      data-testid={`ref-panel-${testKey}`}
    >
      <Card className="border-teal-200 bg-teal-50/40">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 text-teal-700">
              <Sparkles className="h-3.5 w-3.5" />
              {ref_config.label} (from Step {ref_config.from_step})
            </span>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="text-[11px] text-teal-700 hover:text-teal-900 font-semibold"
              data-testid={`ref-toggle-${testKey}`}
            >
              {open ? 'Hide' : 'Show'}
            </button>
          </CardTitle>
        </CardHeader>
        {open && (
          <CardContent>
            <div
              className="max-h-[360px] overflow-y-auto text-sm text-foreground/85 prose prose-sm prose-neutral max-w-none prose-headings:text-foreground prose-headings:font-semibold prose-p:text-sm prose-li:text-sm prose-strong:text-foreground bg-white rounded-lg p-3 border border-teal-100"
              data-testid={`ref-content-${testKey}`}
            >
              <ReactMarkdown>{output.generated_content}</ReactMarkdown>
            </div>
          </CardContent>
        )}
      </Card>
    </motion.div>
  );
};

export const StepReferencePanels = ({ currentPromptKey, allOutputs, pathId }) => {
  const refs = STEP_REFERENCES[currentPromptKey];
  if (!refs || refs.length === 0) return null;

  return (
    <div className="mb-4" data-testid="step-reference-panels">
      {refs.length > 1 && (
        <p
          className="text-[10px] uppercase tracking-widest text-teal-700 mb-2 font-bold flex items-center gap-1.5"
          data-testid="step-reference-heading"
        >
          <Sparkles className="h-3 w-3" />
          Your references for this step
        </p>
      )}
      {refs.map((ref_config) => {
        const output = allOutputs.find((o) => o.prompt_key === ref_config.prompt_key);
        return (
          <ReferenceBlock
            key={ref_config.prompt_key}
            output={output}
            ref_config={ref_config}
            pathId={pathId}
          />
        );
      })}
    </div>
  );
};

export default StepReferencePanels;
